import { useCallback, useEffect, useRef, useState } from "react";
import {
  getUnifiedSuggestions,
  resolveSuggestionArea,
  UnifiedSuggestItem,
} from "../../apis/search";
import { resolveAreaByText, SelectedLocation } from "./resolveByText";
import { generateSessionToken } from "./sessionToken";

export type { SelectedLocation } from "./resolveByText";

const DEBOUNCE_MS = 300;
const MIN_TERM_LENGTH = 3;

type Options = {
  /** Bias suggestions towards this point. */
  latitude?: number;
  longitude?: number;
  /** "all" drops the scope for the full search-page mix. Default "locations". */
  mode?: "locations" | "all";
};

/**
 * Area/city autosuggest against the unified endpoint's locations scope.
 *
 * One Google autocomplete session spans all keystrokes up to a selection, so the
 * token is generated on first use, sent with every suggest call, reused by the
 * resolve call, then reset.
 */
export function useLocationSuggest(term: string, options: Options = {}) {
  const { latitude, longitude, mode = "locations" } = options;
  const [items, setItems] = useState<UnifiedSuggestItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Settled with no results, as opposed to empty between keystrokes.
  const [isEmpty, setIsEmpty] = useState(false);

  const sessionTokenRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a slow earlier request overwriting a newer one.
  const requestIdRef = useRef(0);

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = generateSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = "";
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = term.trim();
    if (trimmed.length < MIN_TERM_LENGTH) {
      requestIdRef.current += 1;
      setItems([]);
      setIsEmpty(false);
      setIsLoading(false);
      return;
    }

    // Loading starts with the request, not the keystroke, so the current list
    // stays put while the user is still typing.
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const list = await getUnifiedSuggestions(trimmed, {
          latitude,
          longitude,
          sessionToken: ensureSessionToken(),
          ...(mode === "locations" ? { scope: "locations" as const } : {}),
        });
        if (requestId !== requestIdRef.current) return;
        setItems(list);
        setIsEmpty(list.length === 0);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setItems([]);
        setIsEmpty(true);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, latitude, longitude, mode, ensureSessionToken]);

  /** Degraded-mode fallback — see `resolveAreaByText`. */
  const resolveByText = useCallback(
    (text: string): Promise<SelectedLocation | null> =>
      resolveAreaByText(text, latitude, longitude),
    [latitude, longitude],
  );

  /** Resolve a suggestion to coordinates and close the billing session. */
  const selectItem = useCallback(
    async (item: UnifiedSuggestItem): Promise<SelectedLocation | null> => {
      const placeId = item.data?.place_id ?? null;
      const label = [item.displayText, item.subtitle].filter(Boolean).join(", ");

      // Degrade mode returns Azure locations, which already carry coordinates.
      if (!placeId) {
        const { latitude: itemLat, longitude: itemLng } = item.data ?? {};
        if (itemLat != null && itemLng != null) {
          return {
            text: label,
            simpleText: item.displayText,
            latitude: itemLat,
            longitude: itemLng,
            placeId: null,
          };
        }
        return resolveByText(label);
      }

      const area = await resolveSuggestionArea({
        placeId,
        sessionToken: sessionTokenRef.current || null,
      });
      resetSessionToken();
      if (!area) return resolveByText(label);

      return {
        text: area.address || label,
        simpleText: area.name || item.displayText,
        latitude: area.lat,
        longitude: area.lng,
        placeId,
      };
    },
    [resetSessionToken, resolveByText],
  );

  /** Resolve free text (the "press enter to search" row) to coordinates. */
  const selectFreeText = useCallback(
    async (text: string): Promise<SelectedLocation | null> => {
      const trimmed = text.trim();
      if (!trimmed) return null;

      const area = await resolveSuggestionArea({
        query: trimmed,
        latitude,
        longitude,
      });
      resetSessionToken();
      if (!area) return resolveByText(trimmed);

      return {
        text: area.address || trimmed,
        simpleText: area.name || trimmed,
        latitude: area.lat,
        longitude: area.lng,
        placeId: area.place_id,
      };
    },
    [latitude, longitude, resetSessionToken, resolveByText],
  );

  /** The live token, for handing a selection to another dispatcher. */
  const getSessionToken = useCallback(() => ensureSessionToken(), [ensureSessionToken]);

  return {
    items,
    isLoading,
    isEmpty,
    selectItem,
    selectFreeText,
    getSessionToken,
    resetSessionToken,
  };
}
